const directionalPropertyMap: Record<string, string> = {
  'padding-left': 'padding-inline-start',
  'padding-right': 'padding-inline-end',
  'margin-left': 'margin-inline-start',
  'margin-right': 'margin-inline-end',
};

function isEscaped(text: string, index: number) {
  let backslashCount = 0;
  for (let cursor = index - 1; cursor >= 0 && text[cursor] === '\\'; cursor -= 1) {
    backslashCount += 1;
  }
  return backslashCount % 2 === 1;
}

function normalizeDeclaration(declarationText: string) {
  let colonIndex = -1;
  let quote = '';
  let comment = false;
  let depth = 0;
  for (let index = 0; index < declarationText.length; index += 1) {
    const char = declarationText[index];
    const nextChar = declarationText[index + 1];
    if (comment) {
      if (char === '*' && nextChar === '/') {
        comment = false;
        index += 1;
      }
    } else if (quote) {
      if (char === quote && !isEscaped(declarationText, index)) quote = '';
    } else if (char === '/' && nextChar === '*') {
      comment = true;
      index += 1;
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    } else if (char === ':' && depth === 0) {
      colonIndex = index;
      break;
    }
  }
  if (colonIndex < 0) return declarationText;

  const propertyText = declarationText.slice(0, colonIndex);
  const propertyMatch = propertyText.match(/^(\s*(?:\/\*[\s\S]*?\*\/\s*)*)([^\s]+)(\s*)$/);
  if (!propertyMatch) return declarationText;
  const logicalProperty = directionalPropertyMap[propertyMatch[2].toLowerCase()];
  if (!logicalProperty) return declarationText;
  return `${propertyMatch[1]}${logicalProperty}${propertyMatch[3]}${declarationText.slice(colonIndex)}`;
}

export function normalizeDirectionalStyleText(styleText: string | null | undefined = '') {
  const source = styleText || '';
  const declarations: string[] = [];
  let start = 0;
  let quote = '';
  let comment = false;
  let depth = 0;

  for (let index = 0; index <= source.length; index += 1) {
    const char = source[index];
    const nextChar = source[index + 1];
    if (comment) {
      if (char === '*' && nextChar === '/') {
        comment = false;
        index += 1;
      }
    } else if (quote) {
      if (char === quote && !isEscaped(source, index)) quote = '';
    } else if (char === '/' && nextChar === '*') {
      comment = true;
      index += 1;
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    } else if ((char === ';' && depth === 0) || index === source.length) {
      declarations.push(normalizeDeclaration(source.slice(start, index)));
      start = index + 1;
    }
  }
  return declarations.join(';');
}

const cssStylePrototype = typeof CSSStyleDeclaration === 'undefined' ? undefined : CSSStyleDeclaration.prototype;
// Capture the platform methods once. View replaces style.setProperty for MachPro's
// public setProperty API, so calling style.setProperty from this adapter would recurse.
const nativeSetProperty = cssStylePrototype?.setProperty;
const nativeRemoveProperty = cssStylePrototype?.removeProperty;

export function installDirectionalStyleAccessors(
  style: CSSStyleDeclaration,
  onUpdate?: (property: string, value: unknown) => void,
) {
  [
    ['paddingLeft', 'padding-inline-start'],
    ['paddingRight', 'padding-inline-end'],
    ['marginLeft', 'margin-inline-start'],
    ['marginRight', 'margin-inline-end'],
  ].forEach(([physicalProperty, logicalProperty]) => {
    try {
      Object.defineProperty(style, physicalProperty, {
        configurable: true,
        get: () => style.getPropertyValue(logicalProperty),
        set: (value) => {
          if (onUpdate) {
            onUpdate(logicalProperty, value);
          } else if (value === undefined || value === null || value === '') {
            nativeRemoveProperty?.call(style, logicalProperty);
          } else {
            nativeSetProperty?.call(style, logicalProperty, String(value));
          }
        },
      });
    } catch {
      // Older WebViews may reject redefining CSSStyleDeclaration properties.
    }
  });
}

export function setNativeStyleProperty(style: CSSStyleDeclaration, property: string, value: unknown) {
  if (value === undefined || value === null || value === '') {
    nativeRemoveProperty?.call(style, property);
  } else {
    nativeSetProperty?.call(style, property, String(value));
  }
}
